import { useEffect, useRef } from 'react';

import { afterNextPaint, getNewRelicApi } from '../newRelicPageAction/newRelicPageAction';

let hasEmittedInitialLoad = false;
let isCancelled = false;
let initialLoadOrigin = 0;
let isClockRestarted = false;
let lastVisibilityChangeAt = -1;

const isDocumentHidden = (): boolean => typeof document !== 'undefined' && document.hidden;

const recordVisibilityChange = (): void => {
    lastVisibilityChangeAt = performance.now();
};

if (typeof document !== 'undefined') {
    document.addEventListener('visibilitychange', recordVisibilityChange);
}

const wasBackgroundedSince = (origin: number): boolean => isDocumentHidden() || lastVisibilityChangeAt >= origin;

export const cancelLoadingIndicatorDisappearsTelemetry = (): void => {
    isCancelled = true;
};

export const startLoadingIndicatorDisappearsClock = (): void => {
    if (hasEmittedInitialLoad) return;
    initialLoadOrigin = performance.now();
    isCancelled = false;
    isClockRestarted = true;
};

export const hasLoadingIndicatorDisappearsClockRestarted = (): boolean => isClockRestarted;

export const hasEmittedLoadingIndicatorDisappearsTelemetry = (): boolean => hasEmittedInitialLoad;

export const useLoadingIndicatorDisappearsTelemetry = (
    pageActionName: string,
    ready: boolean,
    attributes?: Record<string, unknown>
): void => {
    const mountTimeRef = useRef(performance.now());
    const hasFiredRef = useRef(false);
    const attributesRef = useRef(attributes);
    attributesRef.current = attributes;

    useEffect(() => {
        if (!ready || hasFiredRef.current || isCancelled) return;
        hasFiredRef.current = true;

        const mountTime = mountTimeRef.current;
        const readyTime = performance.now();

        const emit = () => {
            try {
                if (isCancelled) return;

                const newrelic = getNewRelicApi();
                if (!newrelic?.addPageAction) return;

                const isInitialLoad = !hasEmittedInitialLoad;
                hasEmittedInitialLoad = true;

                const backgroundedDuringLoad = wasBackgroundedSince(isInitialLoad ? initialLoadOrigin : mountTime);
                const endTime = backgroundedDuringLoad ? readyTime : performance.now();

                newrelic.addPageAction(pageActionName, {
                    loadingIndicatorDisappearsDuration: isInitialLoad ? endTime - initialLoadOrigin : endTime - mountTime,
                    mountToPaintCompleteMs: endTime - mountTime,
                    isInitialLoad,
                    backgroundedDuringLoad,
                    ...attributesRef.current
                });
            } catch {
                // telemetry must never break the UI
            }
        };

        if (isDocumentHidden()) {
            emit();
        } else {
            afterNextPaint(emit);
        }
    }, [ready, pageActionName]);
};

export const __resetInitialLoadTrackingForTests = (): void => {
    hasEmittedInitialLoad = false;
    isCancelled = false;
    initialLoadOrigin = 0;
    isClockRestarted = false;
    lastVisibilityChangeAt = -1;
};
